import type { FanoutSlice } from '~/api/hooks/useTimeframeFanout'

/** Each row has a required `timeframe` label plus numeric series values keyed by series name.
 *  Typed as Record<string, string | number> so TS satisfies the index signature while
 *  recharts (which reads rows as plain objects) still gets the correct values. */
export type TimeframeBarRow = Record<string, string | number>

export interface TimeframeBarData {
  data: TimeframeBarRow[]
  series: string[]
}

const TOTAL = 'total'

/** Pure: per-timeframe walk slices -> recharts bar rows + series keys.
 *  Ungrouped -> one 'total' series (sum, skip null). Grouped -> one series per dim value. */
export function toTimeframeBars(slices: FanoutSlice[]): TimeframeBarData {
  const seriesSet = new Set<string>()
  const data = slices.map((s) => {
    const row: TimeframeBarRow = { timeframe: s.timeframe.label }
    for (const r of s.rows) {
      if (r.value == null) continue
      // dims:{} is truthy but empty — treat as the total (ungrouped) series key
      const key =
        r.dims && Object.keys(r.dims).length > 0 ? Object.values(r.dims).join(' / ') : TOTAL
      seriesSet.add(key)
      row[key] = ((row[key] as number | undefined) ?? 0) + r.value
    }
    return row
  })
  return { data, series: [...seriesSet] }
}
